'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Lock, ShieldCheck } from 'lucide-react';

export const AUTH_STORAGE_KEY = 'mindflow_authenticated';
export const ADMIN_USERNAME = 'Admin';
export const ADMIN_PASSWORD = 'admin123';

const videoInlineProps = { 'webkit-playsinline': 'true' } as const;

export default function LoginLanding() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isReady, setIsReady] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  useEffect(() => {
    const authenticated = window.localStorage.getItem(AUTH_STORAGE_KEY) === 'true';
    if (authenticated && !isPreview) {
      router.replace('/dashboard');
      return;
    }
    setIsReady(true);
  }, [isPreview, router]);

  const handleLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, 'true');
      setError('');
      router.replace('/dashboard');
      return;
    }

    setError('Credenziali non valide');
  };

  if (!isReady) {
    return (
      <div className="min-h-screen p-8 animate-pulse" style={{ background: '#FAFAF7' }}>
        <div
          className="max-w-md mx-auto mt-24 h-96 rounded-3xl"
          style={{ background: '#EAF0EA' }}
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-10"
      style={{
        background:
          'radial-gradient(circle at top left, rgba(184,206,188,0.35), transparent 32%), linear-gradient(180deg, #FAFAF7 0%, #F4F7F3 100%)',
      }}
    >
      <div className="w-full max-w-5xl grid gap-6 lg:grid-cols-[1.15fr_0.85fr] items-start">
        {!isPreview && (
          <div
            className="rounded-[28px] p-6 lg:p-7"
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid #DCE6DD',
              boxShadow: '0 24px 80px rgba(108, 133, 111, 0.12)',
            }}
          >
            <div className="mb-5">
              <p className="text-xs uppercase tracking-[0.18em]" style={{ color: '#9BA8A0' }}>
                Presentazione studio
              </p>
              <h2 className="text-2xl font-semibold mt-2" style={{ color: '#374151' }}>
                MindFlow per psicologi e psicoterapeuti
              </h2>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#6B7280' }}>
                Una panoramica rapida dell&apos;esperienza MindFlow, con agenda, pazienti, diario
                clinico e fatturazione in un unico flusso di lavoro.
              </p>
            </div>

            <video
              {...videoInlineProps}
              src="/Video_Piscologo_mod.mp4"
              poster="/poster_psicologo.jpg"
              controls
              playsInline
              preload="metadata"
              className="w-full rounded-xl shadow-lg"
            />
          </div>
        )}

        <div
          className="w-full rounded-[28px] p-8"
          style={{
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid #DCE6DD',
            boxShadow: '0 24px 80px rgba(108, 133, 111, 0.12)',
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: '#EAF0EA', color: '#4A7A4D' }}
            >
              <ShieldCheck size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em]" style={{ color: '#9BA8A0' }}>
                Accesso protetto
              </p>
              <h1 className="text-2xl font-semibold" style={{ color: '#374151' }}>
                Login MindFlow
              </h1>
            </div>
          </div>

          {!isPreview && (
            <div
              className="mb-6 rounded-2xl p-4"
              style={{ background: '#F4F7F3', border: '1px solid #DCE6DD' }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: '#6C8570' }}
              >
                Credenziali demo
              </p>
              <p className="text-sm" style={{ color: '#4B5A52' }}>
                User: <strong>Admin</strong>
              </p>
              <p className="text-sm mt-1" style={{ color: '#4B5A52' }}>
                Password: <strong>admin123</strong>
              </p>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleLogin}>
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: '#8D9A91' }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Inserisci username"
                className="w-full px-4 py-3 rounded-2xl text-sm outline-none"
                style={{ background: '#FAFAF7', border: '1px solid #DCE6DD', color: '#374151' }}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: '#8D9A91' }}>
                Password
              </label>
              <div className="relative">
                <Lock
                  size={15}
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                  style={{ color: '#A7B4AB' }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Inserisci password"
                  className="w-full pl-11 pr-4 py-3 rounded-2xl text-sm outline-none"
                  style={{ background: '#FAFAF7', border: '1px solid #DCE6DD', color: '#374151' }}
                />
              </div>
            </div>

            {error && (
              <div
                className="rounded-2xl px-4 py-3 text-sm"
                style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-150 active:scale-[0.99]"
              style={{ background: '#6F9173', color: 'white' }}
            >
              Accedi
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
